import { useTitle } from "@/core/context/TitleContext";
import { useEffect, useState } from "react";
import { DataTable } from "@/components/molecules/DataTable/DataTable";
import { useQueryClient } from "@tanstack/react-query";
import { FormStatusEnum } from "@/components/molecules/FormWrapper/FormWrapper.types";
import { FormHeader } from "@/components/molecules/FormHeader/FormHeader";
import { Loading } from "@/components/atoms/Loading/Loading";
import { columns, InstrumentFormData, instrumentKeys } from "@/modules/instruments/infrastructure/InstrumentConstants";
import { InstrumentEntity, InstrumentEntityKV } from "@/modules/instruments/domain/entities/InstrumentEntity";
import { useInstrumentConfigData } from "@/modules/instruments/application/hooks/useInstrumentData";
import { useCreateInstrument, useDeleteInstrument, useUpdateInstrument } from "@/modules/instruments/application/hooks/useInstrumentQueries";
import { InstrumentForm } from "@/modules/instruments/presentation/InstrumentForm";

export const InstrumentsPage = () => {
  const [formStatus, setFormStatus] = useState<FormStatusEnum>(FormStatusEnum.List);
  const [instrumentSelected, setInstrumentSelected] = useState<InstrumentEntity>();
  const [instrumentTypeSelected, setInstrumentTypeSelected] = useState("0");
  const [, setFormKey] = useState(0);
  const queryClient = useQueryClient();
  const { setTitle } = useTitle();
  const {
    instruments,
    isLoading,
    instrumentTypeOptions,
    instrumentsRefetch,
  } = useInstrumentConfigData();

  // hooks
  const createInstrumentMutation = useCreateInstrument();
  const updateInstrumentMutation = useUpdateInstrument();
  const deleteInstrumentMutation = useDeleteInstrument();

  // effects
  useEffect(() => {
    setTitle("Ordenes");
  }, []);

  const onSubmit = async (formData: InstrumentFormData) => {
    if (formStatus === FormStatusEnum.Add) {
      const { id, ...payload } = formData;
      await createInstrumentMutation.mutateAsync(payload);
    } else {
      await updateInstrumentMutation.mutateAsync({ id: formData.id ?? "0", ...formData });
    }

    await queryClient.invalidateQueries({ queryKey: instrumentKeys.all });
    setFormStatus(FormStatusEnum.List);
    setInstrumentSelected(undefined);
  };

  const onDelete = async (id: string) => {
    await deleteInstrumentMutation.mutateAsync(id);
    await queryClient.invalidateQueries({ queryKey: instrumentKeys.all });
    setFormStatus(FormStatusEnum.List);
    setInstrumentSelected(undefined);
  };

  const handleChangeFormStatus = (statusOrUpdater: FormStatusEnum | ((prevState: FormStatusEnum) => FormStatusEnum)) => {
    const nextStatus = typeof statusOrUpdater === 'function'
      ? (statusOrUpdater as (prev: FormStatusEnum) => FormStatusEnum)(formStatus)
      : statusOrUpdater;

    if (nextStatus === FormStatusEnum.List) {
      setInstrumentSelected(undefined);
      setInstrumentTypeSelected("0");
      setFormKey(prev => prev + 1);
    }

    setFormStatus(nextStatus);
  };

  if (
    isLoading ||
    createInstrumentMutation.isPending ||
    updateInstrumentMutation.isPending ||
    deleteInstrumentMutation.isPending
  ) return <Loading />;

  return (
    <>
      <FormHeader
        title="Instrumentos"
        changeFormStatus={handleChangeFormStatus}
        formStatus={formStatus}
        selectItem={setInstrumentSelected}
        refetch={instrumentsRefetch}
        canAdd
      />

      {formStatus === FormStatusEnum.List ? (
        <DataTable
          data={instruments}
          columns={columns}
          tableHeadClass="bg-primary-500 text-white"
          pageSize={10}
          changeFormStatus={setFormStatus}
          selectItem={setInstrumentSelected}
        />
      ) : (
        <InstrumentForm
          onSubmit={onSubmit}
          onDelete={onDelete}
          formStatus={formStatus}
          instrumentSelected={instrumentSelected}
          instrumentTypeOptions={instrumentTypeOptions}
        />
      )}
    </>
  );
};
